import { create } from "zustand";
import { devtools } from "zustand/middleware";
import useAuthStore from "./authStore";
import { WsMessageType } from "../models/wsModels";
import useDraftingStore from "./draftingStore";
import { useTeamsStore } from "./teamsStore";
import { toast } from "react-toastify";

interface WebsocketStore {
  message: string;

  socket: WebSocket | null;
  socketState: 0 | 1 | 2 | 3;

  setWebSocket: (socket: WebSocket | null) => void;
  setSocketState: (socketState: 0 | 1 | 2 | 3) => void;
  connectWebSocket: (email: string) => void;
  retryOnDisconnect: boolean;
  disconnectWebSocket: () => void;

  sendMessage: (message: any) => void;

  draftPick: (
    seasonId: string,
    playerId: string,
    teamId: string,
    userId: string
  ) => void;
}

const useWebSocketStore = create<WebsocketStore>()(
  devtools(
    (set, get) => ({
      message: "",
      socket: null,
      socketState: WebSocket.CLOSED,
      setWebSocket: (socket) => set({ socket }),
      setSocketState: (socketState) => set({ socketState }),
      retryOnDisconnect: true,
      connectWebSocket: (email) => {
        return; // sockets are not being used.
        if (
          get().socket &&
          (get().socketState === WebSocket.CONNECTING ||
            get().socketState === WebSocket.OPEN)
        ) {
          return;
        }
        set({ socketState: WebSocket.CONNECTING });

        const url = window.location.host;
        const wsProtocol =
          window.location.protocol === "https:" ? "wss:" : "ws:";
        const socket = new WebSocket(`${wsProtocol}//${url}/ws/${email}`);
        socket.onopen = () => {
          set({ socketState: WebSocket.OPEN, socket, retryOnDisconnect: true });
          const currentUser = useAuthStore.getState().currentUser;
          if (
            !currentUser ||
            !currentUser?.tokens ||
            !currentUser?.tokens?.accessToken
          ) {
            get().disconnectWebSocket();
            set({
              message: "User not authenticated. Try refreshing the page.",
              socketState: WebSocket.CLOSED,
            });
            console.log(
              "nc --> User not authenticated. Try refreshing the page.",
              currentUser
            );
            toast.error("User not authenticated. Try refreshing the page.");
            return;
          }
          console.log("sending auth message to websocket on open.");
          socket.send(
            JSON.stringify({
              type: WsMessageType.Auth,
              token: currentUser?.tokens?.accessToken,
            })
          );
        };

        socket.onmessage = (event) => {
          const message = JSON.parse(event.data);
          const { type } = message;

          console.log(
            "nc --> message from websocket:",
            message,
            "socket state",
            socket.readyState,
            type
          );

          switch (type) {
            case "authError":
              // navigate to login page
              console.log("nc --> auth error from websocket:", message);
              toast.error("Authentication error. Please login again.");
              useAuthStore.getState().logout();
              break;
              case WsMessageType.SeasonDraftObject:
              useDraftingStore.getState().setSeasonDraftObject(message.payload);
              break;
            case WsMessageType.TimerTick:
              useDraftingStore
                .getState()
                .setTimeLeftInSeconds(message.timeLeftInSeconds);
              break;

            case WsMessageType.FetchMyTeamDetails:
              useTeamsStore.getState().getTeamDetails(message.teamId);
              break;
            case WsMessageType.AvailablePlayers:
              useDraftingStore.getState().setAvailablePlayers(message.payload);
              break;
            case WsMessageType.Error:
              console.error("nc --> error from websocket:", message);
              toast.error(message.message);
              break;
            case WsMessageType.ConnectionLimitExceeded:
              console.error(
                "nc --> connection limit exceeded from websocket:",
                message
              );
              toast.error(message.payload);
              set({ retryOnDisconnect: false });

              break;

            default:
              console.log("nc --> unknown type from websocket:", message);
              break;
          }
        };

        socket.onclose = () => {
          if (get().retryOnDisconnect) {
            setTimeout(() => {
              console.log("nc --> reconnecting to websocket");
              set({ socketState: WebSocket.CLOSED });
              set({ socket: null });
              set({ retryOnDisconnect: true });
              get().connectWebSocket(email);
            }, 5000);
          }
          set({ socket: null });
          set({ socketState: WebSocket.CLOSED });
        };
        set({ socket });
      },
      disconnectWebSocket: () => {
        const socket = get().socket;
        if (socket) {
          set({ retryOnDisconnect: false });
          socket.close();
        }
      },

      draftPick: (
        seasonId: string,
        playerId: string,
        teamId: string,
        userId: string
      ) => {
        const socket = get().socket;
        if (!socket) {
          console.log("nc --> socket not available to send message");
          toast.error("Unable to draft player. Please logout and login.");
          return;
        }
        sendMessage(
          { type: WsMessageType.DraftPickByTeam, seasonId, playerId, teamId, userId },
          socket
        );
      },


      sendMessage: (message: any) => {
        const socket = get().socket;
        if (!socket && get().retryOnDisconnect) {
          const userEmail = useAuthStore.getState().currentUser?.email;
          if (userEmail) {
            get().connectWebSocket(userEmail);
            return;
          } else {
            console.log("nc --> socket not available to send message");
            return;
          }
        } else if (socket) {
          sendMessage(message, get().socket);
        } else {
          console.log("nc --> socket not available to send message");
        }
      },
    }),
    { name: "draftingStore", enabled: process.env.NODE_ENV === "development" }
  )
);

const sendMessage = (message: any, socket: WebSocket | null) => {
  console.log(
    "sending message to websocket:",
    message,
    "socket state",
    socket?.readyState
  );
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(message));
  }
};

export default useWebSocketStore;
